import React, { useState, useEffect } from 'react';
import '../styles/FriendsPage.css';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import { CustomModal } from '../components/CustomModal';
import { referralAPI, ReferralReward as IReferralReward } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

// Define a reward interface to match the API response
interface Reward {
  friendsRequired: number;
  diamondsReward: number;
  claimed: boolean;
  available: boolean;
}

// Функция для правильного склонения слова "друзья"
const getFriendsNoun = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastDigit === 1 && lastTwoDigits !== 11) {
    return 'друга'; // 1, 21, 31, ... но не 11
  } else if (
    (lastDigit >= 2 && lastDigit <= 4) &&
    !(lastTwoDigits >= 12 && lastTwoDigits <= 14)
  ) {
    return 'друзей'; // 2-4, 22-24, 32-34, ... но не 12-14
  } else {
    return 'друзей'; // 0, 5-20, 25-30, ...
  }
};

const BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'Saper_star_bot';

const FriendsPage: React.FC = () => {
  const { stars, energy, diamonds, addStars, addEnergy, addDiamonds, updateDiamonds } = useGameStore();

  // Состояние для модального окна
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    buttons: [] as Array<{id: string, text: string, type?: string}>,
    onClose: (id: string) => {}
  });
  
  // Состояние для реферальной системы
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [invitedFriends, setInvitedFriends] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Состояние для всех возможных наград
  const [allRewards, setAllRewards] = useState<Reward[]>([]);
  
  // Вычислим видимые награды (окно из 3 наград)
  const visibleRewards = React.useMemo(() => {
    // Если наград нет, возвращаем пустой массив
    if (allRewards.length === 0) return [];

    // Сначала сортируем награды по количеству требуемых друзей
    const sortedRewards = [...allRewards].sort((a, b) => 
      a.friendsRequired - b.friendsRequired
    );

    // Находим индекс первой неполученной награды
    const firstUnclaimedIndex = sortedRewards.findIndex(r => !r.claimed);
    
    // Если все награды уже получены, показываем последние три
    if (firstUnclaimedIndex === -1) {
      return sortedRewards.slice(Math.max(0, sortedRewards.length - 3));
    }
    
    // Иначе показываем 3 награды, начиная с первой неполученной
    return sortedRewards.slice(firstUnclaimedIndex, firstUnclaimedIndex + 3);
  }, [allRewards]);
  
  // Состояние для анимации получения награды
  const [rewardAnimation, setRewardAnimation] = useState({
    show: false,
    amount: 0
  });
  
  // Состояние загрузки награды
  const [claimingReward, setClaimingReward] = useState(false);

  // Функция для преобразования данных API в формат для отображения
  const transformRewards = (apiRewards: { count: number, diamonds: number }[], totalReferrals: number): Reward[] => {
    // Генерируем награды для каждого шага (3, 6, 9, 12...)
    const rewardStep = 3;
    const maxRewards = 20; // Максимальное количество наград для отображения
    
    // Создаем массив наград
    const rewards: Reward[] = [];
    
    // Генерируем награды по шагам
    for (let i = 1; i <= maxRewards; i++) {
      const friendsRequired = i * rewardStep;
      // Награда доступна, если количество приглашенных друзей больше или равно требуемому
      const available = totalReferrals >= friendsRequired;
      
      // Проверяем, есть ли эта награда в списке доступных для получения
      // Если награда есть в apiRewards, значит она доступна для получения (не получена)
      const availableForClaim = apiRewards.some(r => r.count === friendsRequired);
      
      // Награда считается полученной, если она доступна по количеству друзей,
      // но отсутствует в списке доступных для получения
      const claimed = available && !availableForClaim;
      
      rewards.push({
        friendsRequired,
        diamondsReward: 1, // Всегда 1 алмаз за каждые 3 друга
        claimed,
        available
      });
    }
    
    return rewards;
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        setLoading(true);
        
        // Получение реферального кода
        const codeResponse = await referralAPI.getReferralCode();
        const code = codeResponse.code;
        
        setReferralCode(code);
        
        // Используем универсальную реферальную ссылку бота
        const fullLink = `https://t.me/${BOT_USERNAME}?start=${code}`;
        setReferralLink(fullLink);
        
        // Получение информации о наградах и рефералах
        const rewardsResponse = await referralAPI.getReferralRewards();
        const totalReferrals = rewardsResponse.totalReferrals;
        const apiRewards = rewardsResponse.rewards || [];
        
        
        // Преобразуем данные API в формат для отображения
        const transformedRewards = transformRewards(apiRewards, totalReferrals);
        setAllRewards(transformedRewards);
        setInvitedFriends(totalReferrals);
        
        setLoading(false);
      } catch (error) {
        console.error("Ошибка при загрузке данных рефералов:", error);
        setLoading(false);
        
        showPopup({
          message: "Ошибка при загрузке данных реферальной системы",
          buttons: [
            { id: "ok", type: "default", text: "ОК" }
          ]
        });
      }
    };
    
    fetchReferralData();
  }, []);
  
  // Обработчик копирования ссылки
  const copyReferralLink = () => {
    try {
      // Создаем временный элемент input для копирования текста
      const tempInput = document.createElement('input');
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      tempInput.value = referralLink;
      document.body.appendChild(tempInput);
      
      // Выделяем текст и пытаемся скопировать
      tempInput.select();
      tempInput.setSelectionRange(0, 99999); // Для мобильных устройств
      
      // Пробуем скопировать через document.execCommand
      const successful = document.execCommand('copy');
      
      // Удаляем временный элемент
      document.body.removeChild(tempInput);
      
      if (successful) {
        showPopup({
          message: "Ссылка скопирована в буфер обмена!",
          buttons: [
            { id: "ok", type: "default", text: "ОК" }
          ]
        });
      } else {
        // Если execCommand не сработал, пробуем через Clipboard API
        navigator.clipboard.writeText(referralLink)
          .then(() => {
            showPopup({
              message: "Ссылка скопирована в буфер обмена!",
              buttons: [
                { id: "ok", type: "default", text: "ОК" }
              ]
            });
          })
          .catch(err => {
            console.error('Не удалось скопировать ссылку: ', err);
            // Показываем сообщение с инструкцией
            showPopup({
              message: "Не удалось скопировать ссылку. Попробуйте скопировать ссылку вручную.",
              buttons: [
                { id: "ok", type: "default", text: "ОК" }
              ]
            });
          });
      }
    } catch (err) {
      console.error('Ошибка при копировании: ', err);
      showPopup({
        message: "Не удалось скопировать ссылку. Попробуйте скопировать ссылку вручную.",
        buttons: [
          { id: "ok", type: "default", text: "ОК" }
        ]
      });
    }
  };

  // Функция показа попапа
  const showPopup = (params: any) => {
    return new Promise<string>((resolve) => {
      setModalConfig({
        title: params.title || 'Уведомление',
        message: params.message,
        buttons: params.buttons || [],
        onClose: (buttonId: string) => {
          setModalIsOpen(false);
          resolve(buttonId);
        }
      });
      setModalIsOpen(true);
    });
  };
  
  // Обработчик получения награды за определенное количество рефералов
  const handleGetReward = async (friendsCount: number) => {
    // Найдем награду по количеству друзей
    const reward = allRewards.find(r => r.friendsRequired === friendsCount);
    if (!reward) return;
    
    const { friendsRequired, diamondsReward, claimed, available } = reward;

    if (available && !claimed && !claimingReward) {
      try {
        // Устанавливаем флаг, что идет процесс получения награды
        setClaimingReward(true);
        
        // Показываем анимацию награды
        setRewardAnimation({
          show: true,
          amount: diamondsReward
        });
        
        // Запрашиваем награду с сервера
        const result = await referralAPI.claimReferralReward(friendsRequired);
        
        // Обновляем баланс алмазов в store
        updateDiamonds(result.totalDiamonds);
        
        // Обновляем состояние наград
        setAllRewards(prev => prev.map(r => 
          r.friendsRequired === friendsRequired 
            ? { ...r, claimed: true } 
            : r
        ));
        
        // Скрываем анимацию через некоторое время
        setTimeout(() => {
          setRewardAnimation({
            show: false,
            amount: 0
          });
          setClaimingReward(false);
        }, 1500);
      } catch (error: any) {
        console.error("Ошибка при получении награды:", error);
        
        // Скрываем анимацию
        setRewardAnimation({
          show: false,
          amount: 0
        });
        setClaimingReward(false);
        
        // Показываем сообщение об ошибке
        const errorMessage = error.response?.data?.error || "Произошла ошибка при получении награды";
        showPopup({
          message: errorMessage,
          buttons: [
            { id: "ok", type: "default", text: "ОК" }
          ]
        });
      }
    } else if (claimed) {
      showPopup({
        message: "Вы уже получили эту награду",
        buttons: [
          { id: "ok", type: "default", text: "ОК" }
        ]
      });
    } else if (!available) {
      showPopup({
        message: `Пригласите еще ${friendsRequired - invitedFriends} друзей для получения награды!`,
        buttons: [
          { id: "ok", type: "default", text: "Понятно" }
        ]
      });
    }
  };
  
  // Функция для определения, есть ли еще неполученные награды
  const hasAvailableRewards = () => {
    return allRewards.some(r => r.available && !r.claimed);
  };

  return (
    <div className="friends-page">
      <BackgroundStars />
      
      {/* Счетчик звезд вверху */}
      <div className="stars-counter">
        <span className="stars-value">{stars}</span>
        <span className="stars-icon">⭐</span>
      </div>
      
      <div className="friends-container">
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : (
          <>
            {/* Секция реферальной ссылки */}
            <div className="referral-section">
              <h2 className="section-title">Ваша реферальная ссылка</h2>
              <div className="referral-link-container">
                <input 
                  className="referral-link" 
                  value={referralLink} 
                  readOnly 
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button className="copy-button" onClick={copyReferralLink}>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </button>
              </div>
              
              {/* Награда за приглашение */}
              <div className="reward-info">
                <div className="reward-info-title">Получайте за каждого друга</div>
                <div className="rewards-container">
                  <div className="reward-item">
                    <div className="reward-amount">10</div>
                    <div className="reward-icon">🎟️</div>
                  </div>
                </div>
              </div>
              
              {/* Счетчик приглашенных */}
              <div className="invited-counter">
                <div className="invited-label">Приглашено друзей:</div>
                <div className="invited-value">{invitedFriends}</div>
              </div>
            </div>
            
            {/* Секция бонусов за приглашения */}
            <div className="invitation-rewards">
              <h2 className="section-title">Бонусы за приглашения</h2>
              
              <div className="rewards-info-text">
                За каждых 3 приглашенных друзей вы получаете 1 алмаз.
                Чем больше друзей вы пригласите, тем больше наград сможете получить!
              </div>
              
              {/* Динамические карточки с наградами (всегда только 3) */}
              <AnimatePresence>
                {visibleRewards.map((reward, index) => (
                  <motion.div 
                    className="invitation-reward-card" 
                    key={reward.friendsRequired}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                <div className="reward-card-content">
                  <div className="invitation-milestone">
                        <div className="milestone-number">{reward.friendsRequired}</div>
                        <div className="milestone-text">{getFriendsNoun(reward.friendsRequired)}</div>
                  </div>
                  
                  <div className="reward-details">
                    <div className="reward-item diamond-reward">
                          <div className="reward-amount">{reward.diamondsReward}</div>
                      <div className="reward-icon diamond-icon">💎</div>
                    </div>
                  </div>
                </div>
                
                    <motion.button 
                  className={`get-reward-button ${
                        reward.available 
                          ? reward.claimed 
                        ? 'disabled' 
                        : 'active' 
                      : 'disabled'
                  }`}
                      onClick={() => handleGetReward(reward.friendsRequired)}
                      whileTap={
                        reward.available && !reward.claimed && !claimingReward
                          ? { scale: 0.95 } 
                          : {}
                      }
                      disabled={claimingReward}
                    >
                      {claimingReward ? 'Загрузка...' : reward.claimed ? 'Получено' : 'Получить'}
                    </motion.button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            {/* Анимация получения награды */}
            <AnimatePresence>
              {rewardAnimation.show && (
                <motion.div 
                  className="reward-animation"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.5 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="reward-animation-content">
                    <div className="reward-animation-text">+{rewardAnimation.amount}</div>
                    <div className="reward-animation-icon">💎</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
      
      <NavigationBar />
      
      <CustomModal
        isOpen={modalIsOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        buttons={modalConfig.buttons}
        onClose={(buttonId) => modalConfig.onClose?.(buttonId)}
      />
    </div>
  );
};

export default FriendsPage; 